import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Users, Wallet, CreditCard, Eye, X, TrendingUp, TrendingDown, Calendar, FileText, ChevronDown, ChevronUp, Trash2, Download, Edit, DollarSign, Receipt, Banknote } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const EmployeeAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedPayroll, setExpandedPayroll] = useState(null);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const [expandedExpense, setExpandedExpense] = useState(null);
  const [expandedPettyCash, setExpandedPettyCash] = useState(null);

  useEffect(() => {
    fetchAccounts();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAccounts();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/employee-accounts');
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to fetch employee accounts');
    } finally {
      setLoading(false);
    }
  };

  const viewEmployeeDetails = async (employeeId) => {
    try {
      const response = await axiosInstance.get(`/employee-accounts/${employeeId}`);
      setSelectedEmployee(response.data);
      setShowDetailModal(true);
      setExpandedPayroll(null);
      setExpandedLoan(null);
      setExpandedExpense(null);
      setExpandedPettyCash(null);
    } catch (error) {
      toast.error('Failed to fetch employee details');
    }
  };

  const deleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete "${employeeName}"? This will also delete all related payrolls and loans.`)) {
      return;
    }
    try {
      await axiosInstance.delete(`/employees/${employeeId}`);
      toast.success('Employee deleted successfully');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const deletePayroll = async (payrollId) => {
    if (!window.confirm('Are you sure you want to delete this payroll?')) return;
    try {
      await axiosInstance.delete(`/payroll/${payrollId}`);
      toast.success('Payroll deleted');
      viewEmployeeDetails(selectedEmployee.employee.id);
    } catch (error) {
      toast.error('Failed to delete payroll');
    }
  };

  const deleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan?')) return;
    try {
      await axiosInstance.delete(`/loans/${loanId}`);
      toast.success('Loan deleted');
      viewEmployeeDetails(selectedEmployee.employee.id);
    } catch (error) {
      toast.error('Failed to delete loan');
    }
  };

  const exportEmployeePDF = async (employeeId) => {
    try {
      const response = await axiosInstance.get(`/employee-accounts/${employeeId}`);
      const data = response.data;
      const emp = data.employee;
      const summary = data.summary;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(79, 70, 229);
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Employee Account Statement', pageWidth / 2, 18, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 28, { align: 'center' });
      
      // Employee Info Box
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(14, 50, pageWidth - 28, 35, 3, 3, 'F');
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(emp.full_name, 20, 62);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Position: ${emp.position || '-'}  |  Department: ${emp.department || '-'}  |  Email: ${emp.email}`, 20, 72);
      doc.text(`Base Salary: ${emp.salary?.toLocaleString()} SAR  |  Hire Date: ${emp.hire_date || '-'}`, 20, 80);
      
      // Summary Cards
      let yPos = 95;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Account Summary', 14, yPos);
      
      yPos += 8;
      const cardWidth = (pageWidth - 42) / 5;
      const cards = [
        { label: 'Total Paid', value: `${summary.total_net_paid?.toLocaleString()} SAR`, color: [34, 197, 94] },
        { label: 'Total Expenses', value: `${(summary.total_expenses || 0)?.toLocaleString()} SAR`, color: [239, 68, 68] },
        { label: 'Petty Cash', value: `${(summary.total_petty_cash || 0)?.toLocaleString()} SAR`, color: [245, 158, 11] },
        { label: 'Pending', value: `${(summary.pending_balance || 0)?.toLocaleString()} SAR`, color: [249, 115, 22] },
        { label: 'Loan Balance', value: `${summary.loan_remaining?.toLocaleString()} SAR`, color: [168, 85, 247] }
      ];
      
      cards.forEach((card, i) => {
        const x = 14 + (i * (cardWidth + 4));
        doc.setFillColor(...card.color);
        doc.roundedRect(x, yPos, cardWidth, 20, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(card.label, x + 3, yPos + 8);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(card.value, x + 3, yPos + 16);
      });
      
      // Payroll History Table
      yPos += 35;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Payroll History (${data.payrolls.length} records)`, 14, yPos);
      
      if (data.payrolls.length > 0) {
        const payrollData = data.payrolls.map(p => [
          p.payroll_number,
          `${p.period_start} to ${p.period_end}`,
          `+${p.total_earnings?.toLocaleString()}`,
          `-${p.total_deductions?.toLocaleString()}`,
          `${p.net_salary?.toLocaleString()} SAR`,
          p.status.toUpperCase()
        ]);
        
        autoTable(doc, {
          startY: yPos + 5,
          head: [['Payroll #', 'Period', 'Earnings', 'Deductions', 'Net Salary', 'Status']],
          body: payrollData,
          theme: 'striped',
          headStyles: { fillColor: [79, 70, 229], fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          columnStyles: {
            2: { textColor: [34, 197, 94] },
            3: { textColor: [239, 68, 68] },
            4: { fontStyle: 'bold' }
          }
        });
      }
      
      // Loans Table
      if (data.loans.length > 0) {
        const loansStartY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : yPos + 20;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Loans & Advances (${data.loans.length} records)`, 14, loansStartY);
        
        const loanData = data.loans.map(l => [
          l.loan_number,
          l.loan_type === 'loan' ? 'Loan' : 'Advance',
          `${l.amount?.toLocaleString()} SAR`,
          `${l.monthly_deduction?.toLocaleString()} SAR`,
          `${l.remaining_balance?.toLocaleString()} SAR`,
          l.status.toUpperCase()
        ]);
        
        autoTable(doc, {
          startY: loansStartY + 5,
          head: [['Loan #', 'Type', 'Amount', 'Monthly', 'Remaining', 'Status']],
          body: loanData,
          theme: 'striped',
          headStyles: { fillColor: [168, 85, 247], fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          columnStyles: {
            4: { fontStyle: 'bold', textColor: [168, 85, 247] }
          }
        });
      }
      
      // Expenses Table
      if (data.expenses && data.expenses.length > 0) {
        const expensesStartY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : yPos + 20;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Expense Reports (${data.expenses.length} records)`, 14, expensesStartY);
        
        const expenseData = data.expenses.map(e => [
          e.report_no,
          `${e.period_start} - ${e.period_end}`,
          `${e.amount?.toLocaleString()} SAR`,
          e.status,
          e.approver_name || '-'
        ]);
        
        autoTable(doc, {
          startY: expensesStartY + 5,
          head: [['Report #', 'Period', 'Amount', 'Status', 'Approved By']],
          body: expenseData,
          theme: 'striped',
          headStyles: { fillColor: [239, 68, 68], fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          columnStyles: {
            2: { fontStyle: 'bold', textColor: [239, 68, 68] }
          }
        });
      }
      
      // Petty Cash Table
      if (data.petty_cash && data.petty_cash.length > 0) {
        const pcStartY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : yPos + 20;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Petty Cash (${data.petty_cash.length} records)`, 14, pcStartY);
        
        const pcData = data.petty_cash.map(pc => [
          pc.transaction_no,
          pc.fund_name,
          pc.project_name || '-',
          pc.category || '-',
          `${pc.amount?.toLocaleString()} SAR`,
          pc.description?.substring(0, 20) || '-'
        ]);
        
        autoTable(doc, {
          startY: pcStartY + 5,
          head: [['Transaction #', 'Fund', 'Project', 'Category', 'Amount', 'Description']],
          body: pcData,
          theme: 'striped',
          headStyles: { fillColor: [245, 158, 11], fontSize: 9 },
          bodyStyles: { fontSize: 8 },
          columnStyles: {
            4: { fontStyle: 'bold', textColor: [245, 158, 11] }
          }
        });
      }
      
      // Footer
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 250;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Distinct High Road - Accounting System', pageWidth / 2, finalY, { align: 'center' });
      
      // Save
      doc.save(`Employee_Statement_${emp.full_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
      console.error(error);
    }
  };


  const toggleRow = (employeeId) => {
    setExpandedRows(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  if (loading) {
    return <div className="text-center py-12">Loading employee accounts...</div>;
  }

  return (
    <div data-testid="employee-accounts-page">
      <div className="page-header">
        <h2 className="page-title">Employee Accounts</h2>
        <p className="text-gray-500">View all employees with their payroll and payment history</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 rounded-xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6" />
            <span className="text-blue-100">Total Employees</span>
          </div>
          <p className="text-3xl font-bold">{accounts.length}</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-6 h-6" />
            <span className="text-green-100">Total Paid</span>
          </div>
          <p className="text-3xl font-bold">
            {accounts.reduce((sum, a) => sum + (a.summary?.total_net_paid || 0), 0).toLocaleString()}
          </p>
          <p className="text-green-100 text-sm">SAR</p>
        </div>
        
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-5 rounded-xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <Wallet className="w-6 h-6" />
            <span className="text-orange-100">Pending Payment</span>
          </div>
          <p className="text-3xl font-bold">
            {accounts.reduce((sum, a) => sum + (a.summary?.total_net_pending || 0), 0).toLocaleString()}
          </p>
          <p className="text-orange-100 text-sm">SAR</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-5 rounded-xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="w-6 h-6" />
            <span className="text-purple-100">Active Loans</span>
          </div>
          <p className="text-3xl font-bold">
            {accounts.reduce((sum, a) => sum + (a.summary?.loan_remaining || 0), 0).toLocaleString()}
          </p>
          <p className="text-purple-100 text-sm">SAR</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500 to-red-600 p-5 rounded-xl text-white">
          <div className="flex items-center gap-3 mb-2">
            <Receipt className="w-6 h-6" />
            <span className="text-red-100">Total Expenses</span>
          </div>
          <p className="text-3xl font-bold">
            {accounts.reduce((sum, a) => sum + (a.summary?.total_expenses || 0), 0).toLocaleString()}
          </p>
          <p className="text-red-100 text-sm">SAR</p>
        </div>
      </div>

      {/* Employee List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th className="w-10"></th>
              <th>Employee</th>
              <th>Position</th>
              <th>Department</th>
              <th>Base Salary</th>
              <th>Payrolls</th>
              <th>Total Paid</th>
              <th>Expenses</th>
              <th>Petty Cash</th>
              <th>Pending</th>
              <th>Loan Balance</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length > 0 ? (
              accounts.map((account) => (
                <React.Fragment key={account.employee.id}>
                  <tr className="hover:bg-gray-50">
                    <td>
                      <button
                        onClick={() => toggleRow(account.employee.id)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        {expandedRows[account.employee.id] ? 
                          <ChevronUp className="w-4 h-4" /> : 
                          <ChevronDown className="w-4 h-4" />
                        }
                      </button>
                    </td>
                    <td>
                      <div>
                        <p className="font-semibold">{account.employee.full_name}</p>
                        <p className="text-xs text-gray-500">{account.employee.email}</p>
                      </div>
                    </td>
                    <td>{account.employee.position || '-'}</td>
                    <td>{account.employee.department || '-'}</td>
                    <td>{account.employee.salary?.toLocaleString()} SAR</td>
                    <td className="text-center">
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm font-semibold">
                        {account.summary.total_payrolls}
                      </span>
                    </td>
                    <td className="text-green-600 font-semibold">
                      {account.summary.total_net_paid?.toLocaleString()} SAR
                    </td>
                    <td className="text-red-600 font-semibold">
                      {account.summary.total_expenses?.toLocaleString() || 0} SAR
                    </td>
                    <td className="text-amber-600 font-semibold">
                      {account.summary.total_petty_cash?.toLocaleString() || 0} SAR
                    </td>
                    <td className={`font-semibold ${(account.summary.pending_balance || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                      {account.summary.pending_balance?.toLocaleString() || 0} SAR
                    </td>
                    <td className="text-purple-600 font-semibold">
                      {account.summary.loan_remaining?.toLocaleString()} SAR
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => viewEmployeeDetails(account.employee.id)}
                          className="btn-icon"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => exportEmployeePDF(account.employee.id)}
                          className="btn-icon"
                          title="Download PDF Statement"
                        >
                          <Download className="w-4 h-4 text-green-600" />
                        </button>
                        <button
                          onClick={() => deleteEmployee(account.employee.id, account.employee.full_name)}
                          className="btn-icon delete"
                          title="Delete Employee"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Row - Recent Payrolls */}
                  {expandedRows[account.employee.id] && (
                    <tr>
                      <td colSpan="12" className="bg-gray-50 p-4">
                        <div className="grid grid-cols-3 gap-4">
                          {/* Recent Payrolls */}
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              <Wallet className="w-4 h-4" />
                              Recent Payrolls
                            </h4>
                            {account.payrolls.length > 0 ? (
                              <div className="space-y-2">
                                {account.payrolls.slice(0, 3).map((payroll, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded-lg border flex justify-between items-center">
                                    <div>
                                      <p className="font-mono text-sm text-purple-600">{payroll.payroll_number}</p>
                                      <p className="text-xs text-gray-500">{payroll.period_start} - {payroll.period_end}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold">{payroll.net_salary?.toLocaleString()} SAR</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        payroll.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                      }`}>
                                        {payroll.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No payroll records</p>
                            )}
                          </div>
                          
                          {/* Active Loans */}
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              <CreditCard className="w-4 h-4" />
                              Loans & Advances
                            </h4>
                            {account.loans.length > 0 ? (
                              <div className="space-y-2">
                                {account.loans.map((loan, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded-lg border flex justify-between items-center">
                                    <div>
                                      <p className="font-mono text-sm text-purple-600">{loan.loan_number}</p>
                                      <p className="text-xs text-gray-500">
                                        {loan.loan_type === 'loan' ? 'Loan' : 'Advance'} - Monthly: {loan.monthly_deduction?.toLocaleString()} SAR
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold">{loan.remaining_balance?.toLocaleString()} SAR</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        loan.status === 'active' ? 'bg-blue-100 text-blue-700' : 
                                        loan.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                      }`}>
                                        {loan.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No loans or advances</p>
                            )}
                          </div>
                          
                          {/* Expense Reports */}
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              <Receipt className="w-4 h-4" />
                              Expense Reports
                            </h4>
                            {account.expenses && account.expenses.length > 0 ? (
                              <div className="space-y-2">
                                {account.expenses.slice(0, 3).map((expense, idx) => (
                                  <div key={idx} className="bg-white p-3 rounded-lg border flex justify-between items-center">
                                    <div>
                                      <p className="font-mono text-sm text-red-600">{expense.report_no}</p>
                                      <p className="text-xs text-gray-500">{expense.period_start} - {expense.period_end}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-red-600">{expense.amount?.toLocaleString()} SAR</p>
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                        {expense.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">No expense reports</p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center py-12 text-gray-500">
                  No employee accounts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Employee Detail Modal */}
      {showDetailModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" style={{maxWidth: '1200px', width: '95%'}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <Users className="w-6 h-6 inline mr-2 text-purple-600" />
                {selectedEmployee.employee.full_name} - Account Details
              </h3>
              <div className="flex gap-2">
                <button 
                  onClick={() => exportEmployeePDF(selectedEmployee.employee.id)} 
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </button>
                <button onClick={() => setShowDetailModal(false)} className="btn-icon">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="max-h-[75vh] overflow-y-auto space-y-4 p-1">
              {/* Employee Info */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <h4 className="font-semibold text-gray-700 mb-3">Employee Information</h4>
                <div className="grid grid-cols-5 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Full Name</p>
                    <p className="font-semibold">{selectedEmployee.employee.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-semibold">{selectedEmployee.employee.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Position</p>
                    <p className="font-semibold">{selectedEmployee.employee.position || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-semibold">{selectedEmployee.employee.department || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Base Salary</p>
                    <p className="font-semibold text-green-600">{selectedEmployee.employee.salary?.toLocaleString()} SAR</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Hire Date</p>
                    <p className="font-semibold">{selectedEmployee.employee.hire_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Phone</p>
                    <p className="font-semibold">{selectedEmployee.employee.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bank Account</p>
                    <p className="font-semibold">{selectedEmployee.employee.bank_account || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">ID Number</p>
                    <p className="font-semibold">{selectedEmployee.employee.id_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      selectedEmployee.employee.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {selectedEmployee.employee.status || 'active'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-6 gap-3">
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Total Paid</p>
                  <p className="text-lg font-bold text-green-600">{selectedEmployee.summary.total_net_paid?.toLocaleString()} SAR</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-lg font-bold text-orange-600">{selectedEmployee.summary.total_net_pending?.toLocaleString()} SAR</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Total Loans</p>
                  <p className="text-lg font-bold text-blue-600">{selectedEmployee.summary.total_loans?.toLocaleString()} SAR</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Loan Remaining</p>
                  <p className="text-lg font-bold text-purple-600">{selectedEmployee.summary.loan_remaining?.toLocaleString()} SAR</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Total Expenses</p>
                  <p className="text-lg font-bold text-red-600">{selectedEmployee.summary.total_expenses?.toLocaleString() || 0} SAR</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Petty Cash</p>
                  <p className="text-lg font-bold text-amber-600">{selectedEmployee.summary.total_petty_cash?.toLocaleString() || 0} SAR</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${(selectedEmployee.summary.pending_balance || 0) > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                  <p className="text-xs text-gray-500">Pending Balance</p>
                  <p className={`text-lg font-bold ${(selectedEmployee.summary.pending_balance || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {selectedEmployee.summary.pending_balance?.toLocaleString() || 0} SAR
                  </p>
                </div>
              </div>

              {/* Payroll History */}
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="p-3 bg-green-50 border-b flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-700">Payroll History ({selectedEmployee.payrolls.length})</h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {selectedEmployee.payrolls.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Payroll #</th>
                          <th className="px-3 py-2 text-left">Period</th>
                          <th className="px-3 py-2 text-right">Earnings</th>
                          <th className="px-3 py-2 text-right">Deductions</th>
                          <th className="px-3 py-2 text-right">Net Salary</th>
                          <th className="px-3 py-2 text-center">Status</th>
                          <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployee.payrolls.map((p, idx) => (
                          <React.Fragment key={idx}>
                            <tr 
                              className={`border-b hover:bg-blue-50 cursor-pointer ${expandedPayroll === idx ? 'bg-blue-50' : ''}`}
                              onClick={() => setExpandedPayroll(expandedPayroll === idx ? null : idx)}
                            >
                              <td className="px-3 py-2 font-mono text-purple-600">{p.payroll_number}</td>
                              <td className="px-3 py-2">{p.period_start} to {p.period_end}</td>
                              <td className="px-3 py-2 text-right text-green-600">+{p.total_earnings?.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-red-600">-{p.total_deductions?.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right font-semibold">{p.net_salary?.toLocaleString()} SAR</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  p.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {p.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                <div className="flex gap-1 justify-center">
                                  <button
                                    onClick={() => window.location.href = `/payroll?edit=${p.id}`}
                                    className="p-1 hover:bg-blue-100 rounded"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4 text-blue-600" />
                                  </button>
                                  <button
                                    onClick={() => deletePayroll(p.id)}
                                    className="p-1 hover:bg-red-100 rounded"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {expandedPayroll === idx && (
                              <tr className="bg-blue-50">
                                <td colSpan="7" className="px-4 py-3">
                                  <div className="grid grid-cols-4 gap-4 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Basic Salary</p>
                                      <p className="font-bold text-lg">{p.basic_salary?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Allowances</p>
                                      <p className="font-bold text-lg text-green-600">+{p.allowances?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Overtime</p>
                                      <p className="font-bold text-lg text-blue-600">+{p.overtime?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Bonus</p>
                                      <p className="font-bold text-lg text-purple-600">+{p.bonus?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Tax</p>
                                      <p className="font-bold text-lg text-red-600">-{p.tax?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Insurance</p>
                                      <p className="font-bold text-lg text-red-600">-{p.insurance?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Loan Deduction</p>
                                      <p className="font-bold text-lg text-red-600">-{p.loan_deduction?.toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Other Deductions</p>
                                      <p className="font-bold text-lg text-red-600">-{p.other_deductions?.toLocaleString()} SAR</p>
                                    </div>
                                  </div>
                                  {p.notes && (
                                    <div className="mt-3 p-2 bg-yellow-50 rounded text-sm">
                                      <span className="font-semibold">Notes:</span> {p.notes}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-gray-500">No payroll records</p>
                  )}
                </div>
              </div>

              {/* Loans History */}
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="p-3 bg-purple-50 border-b flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-purple-700">Loans & Advances ({selectedEmployee.loans.length})</h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {selectedEmployee.loans.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Loan #</th>
                          <th className="px-3 py-2 text-left">Type</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-right">Monthly</th>
                          <th className="px-3 py-2 text-right">Remaining</th>
                          <th className="px-3 py-2 text-center">Status</th>
                          <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployee.loans.map((l, idx) => (
                          <React.Fragment key={idx}>
                            <tr 
                              className={`border-b hover:bg-purple-50 cursor-pointer ${expandedLoan === idx ? 'bg-purple-50' : ''}`}
                              onClick={() => setExpandedLoan(expandedLoan === idx ? null : idx)}
                            >
                              <td className="px-3 py-2 font-mono text-purple-600">{l.loan_number}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  l.loan_type === 'loan' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  {l.loan_type === 'loan' ? 'Loan' : 'Advance'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right">{l.amount?.toLocaleString()} SAR</td>
                              <td className="px-3 py-2 text-right">{l.monthly_deduction?.toLocaleString()} SAR</td>
                              <td className="px-3 py-2 text-right font-semibold">{l.remaining_balance?.toLocaleString()} SAR</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  l.status === 'active' ? 'bg-blue-100 text-blue-700' : 
                                  l.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {l.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                <div className="flex gap-1 justify-center">
                                  <button
                                    onClick={() => window.location.href = `/payroll?editLoan=${l.id}`}
                                    className="p-1 hover:bg-blue-100 rounded"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4 text-blue-600" />
                                  </button>
                                  <button
                                    onClick={() => deleteLoan(l.id)}
                                    className="p-1 hover:bg-red-100 rounded"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {expandedLoan === idx && (
                              <tr className="bg-purple-50">
                                <td colSpan="7" className="px-4 py-3">
                                  <div className="grid grid-cols-4 gap-4 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Start Date</p>
                                      <p className="font-bold">{l.start_date || '-'}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">End Date</p>
                                      <p className="font-bold">{l.end_date || '-'}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Total Paid</p>
                                      <p className="font-bold text-green-600">{((l.amount || 0) - (l.remaining_balance || 0)).toLocaleString()} SAR</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                      <p className="text-gray-500 text-xs">Progress</p>
                                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                        <div 
                                          className="bg-purple-600 h-2 rounded-full" 
                                          style={{width: `${l.amount ? ((l.amount - (l.remaining_balance || 0)) / l.amount * 100) : 0}%`}}
                                        ></div>
                                      </div>
                                      <p className="text-xs mt-1">{l.amount ? Math.round((l.amount - (l.remaining_balance || 0)) / l.amount * 100) : 0}% paid</p>
                                    </div>
                                  </div>
                                  {l.reason && (
                                    <div className="mt-3 p-2 bg-yellow-50 rounded text-sm">
                                      <span className="font-semibold">Reason:</span> {l.reason}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-gray-500">No loans or advances</p>
                  )}
                </div>
              </div>

              {/* Expense Reports History */}
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="p-3 bg-red-50 border-b flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-red-600" />
                  <h4 className="font-semibold text-red-700">Expense Reports ({selectedEmployee.expenses?.length || 0})</h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {selectedEmployee.expenses && selectedEmployee.expenses.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Report #</th>
                          <th className="px-3 py-2 text-left">Period</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-center">Status</th>
                          <th className="px-3 py-2 text-left">Approved By</th>
                          <th className="px-3 py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployee.expenses.map((exp, idx) => (
                          <React.Fragment key={idx}>
                            <tr 
                              className={`border-b hover:bg-red-50 cursor-pointer ${expandedExpense === idx ? 'bg-red-50' : ''}`}
                              onClick={() => setExpandedExpense(expandedExpense === idx ? null : idx)}
                            >
                              <td className="px-3 py-2 font-mono text-red-600">{exp.report_no}</td>
                              <td className="px-3 py-2">{exp.period_start} to {exp.period_end}</td>
                              <td className="px-3 py-2 text-right font-semibold text-red-600">{exp.amount?.toLocaleString()} SAR</td>
                              <td className="px-3 py-2 text-center">
                                <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                  {exp.status}
                                </span>
                              </td>
                              <td className="px-3 py-2">{exp.approver_name || '-'}</td>
                              <td className="px-3 py-2 text-xs text-gray-500">
                                {exp.created_at ? new Date(exp.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                            {expandedExpense === idx && (
                              <tr className="bg-red-50">
                                <td colSpan="6" className="px-4 py-3">
                                  <div className="bg-white p-3 rounded-lg">
                                    <p className="text-gray-500 text-xs mb-1">Description</p>
                                    <p className="font-semibold">{exp.description || 'No description'}</p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-gray-500">No expense reports</p>
                  )}
                </div>
              </div>

              {/* Petty Cash History */}
              <div className="bg-white border rounded-xl overflow-hidden">
                <div className="p-3 bg-amber-50 border-b flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-amber-600" />
                  <h4 className="font-semibold text-amber-700">Petty Cash ({selectedEmployee.petty_cash?.length || 0})</h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {selectedEmployee.petty_cash && selectedEmployee.petty_cash.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Transaction #</th>
                          <th className="px-3 py-2 text-left">Fund</th>
                          <th className="px-3 py-2 text-left">Project</th>
                          <th className="px-3 py-2 text-left">Category</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployee.petty_cash.map((pc, idx) => (
                          <tr key={idx} className="border-b hover:bg-amber-50">
                            <td className="px-3 py-2 font-mono text-amber-600">{pc.transaction_no}</td>
                            <td className="px-3 py-2">{pc.fund_name}</td>
                            <td className="px-3 py-2 text-purple-600">{pc.project_name || '-'}</td>
                            <td className="px-3 py-2">{pc.category || '-'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-amber-600">{pc.amount?.toLocaleString()} SAR</td>
                            <td className="px-3 py-2 text-xs text-gray-600">{pc.description}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {pc.created_at ? new Date(pc.created_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-gray-500">No petty cash transactions</p>
                  )}
                </div>
              </div>

              {/* Journal Entries */}
              {selectedEmployee.journal_entries && selectedEmployee.journal_entries.length > 0 && (
                <div className="bg-white border rounded-xl overflow-hidden">
                  <div className="p-3 bg-blue-50 border-b flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-700">Journal Entries ({selectedEmployee.journal_entries.length})</h4>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Entry #</th>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-right">Debit</th>
                          <th className="px-3 py-2 text-right">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedEmployee.journal_entries.map((j, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-blue-600">{j.entry_number}</td>
                            <td className="px-3 py-2">{j.date}</td>
                            <td className="px-3 py-2 text-xs">{j.description}</td>
                            <td className="px-3 py-2 text-right">{j.total_debit?.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right">{j.total_credit?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <button onClick={() => setShowDetailModal(false)} className="btn-secondary w-full">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeAccounts;
